import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiResponse } from '@gk/gk-modules';
import _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { Observable, catchError, filter, map, of, shareReplay } from 'rxjs';
import {
  PCLBRPModernizationDetails,
  PCLBRPModernizationDetailsApi,
} from './modernization-details.model';

@Injectable()
export class PCLBRPModernizationDetailsService {
  private modernizationDetails: Observable<PCLBRPModernizationDetails[]>;
  private cachedFieldObservables: {
    [key: string]: { [id: string]: Observable<string> };
  } = {};

  constructor(
    private httpClient: HttpClient,
    private toastrService: ToastrService,
  ) {}

  getModernizationDetailsInfoById(id: string): Observable<string> {
    return this.getFieldById(id, 'info');
  }

  getModernizationDetailsContactDataById(id: string): Observable<string> {
    return this.getFieldById(id, 'contactDetails');
  }

  getModernizationDetailsStartDateById(id: string): Observable<string> {
    return this.getFieldById(id, 'uiDateFrom');
  }

  getModernizationDetailsEndDateById(id: string): Observable<string> {
    return this.getFieldById(id, 'uiDateTo');
  }

  private getFieldById(
    id: string,
    field: keyof PCLBRPModernizationDetails,
  ): Observable<string> {
    if (!this.cachedFieldObservables[field]) {
      this.cachedFieldObservables[field] = {};
    }

    if (!this.cachedFieldObservables[field][id]) {
      this.cachedFieldObservables[field][id] =
        this.getModernizationDetails().pipe(
          map(
            (details) =>
              details.find((detail) => detail.id === id)?.[field] ?? '',
          ),
          catchError((error) => {
            console.error(error);
            return of('');
          }),
          shareReplay(1),
        );
    }

    return this.cachedFieldObservables[field][id];
  }

  getModernizationDetails(): Observable<
    PCLBRPModernizationDetails[] | undefined
  > {
    if (!this.modernizationDetails) {
      this.modernizationDetails = this.httpClient
        .get<
          ApiResponse<PCLBRPModernizationDetailsApi[]>
        >('/api/egib/slo/wylozenie/uwagi')
        .pipe(
          map((data) =>
            data.Response
              ? data.Response.map((reponse) =>
                  PCLBRPModernizationDetails.fromApiToApp(reponse),
                )
              : [],
          ),
          catchError((error) => {
            const message = _.get(error, 'error.ResponseStatus.Message', '');
            if (message) {
              this.toastrService.error(message);
            }
            console.error(error);
            return of(undefined);
          }),
          shareReplay(1),
        );
    }
    return this.modernizationDetails;
  }

  getModernizationDetailsIndexById(id: string): Observable<number> {
    return this.getModernizationDetails().pipe(
      filter(Boolean),
      map((details) => details.findIndex((detail) => detail.id === id)),
    );
  }
}
